import { useEffect, useRef, useState } from 'react';

import { supabase } from '~/utils/supabaseClient';

export interface RealtimeTable {
  table: string;
  filter?: string;
}

export function useRealtimeRefresh({
  channelName,
  enabled = true,
  tables,
  deleteTables = [],
  refresh,
  debounceMs = 200,
}: {
  channelName: string;
  enabled?: boolean;
  tables: RealtimeTable[];
  /** Default replica identity sends only IDs on DELETE, so owner filters cannot match. */
  deleteTables?: string[];
  refresh: () => void | Promise<void>;
  debounceMs?: number;
}): boolean {
  const refreshRef = useRef(refresh);
  const timerRef = useRef<number | null>(null);
  const channelIdRef = useRef(`${channelName}-${crypto.randomUUID()}`);
  const [connected, setConnected] = useState(false);
  const tableKey = tables.map(({ table, filter }) => `${table}:${filter ?? ''}`).join('|');
  const deleteKey = deleteTables.join('|');

  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    if (!enabled || (tables.length === 0 && deleteTables.length === 0)) {
      setConnected(false);
      return;
    }

    const scheduleRefresh = () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        void refreshRef.current();
      }, debounceMs);
    };

    let channel = supabase.channel(channelIdRef.current);
    for (const { table, filter } of tables) {
      channel = channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) },
        scheduleRefresh,
      );
    }
    // These ID-only notifications only invalidate the caller's owner-scoped
    // read. Never use a deleted-row payload as visible application data.
    for (const table of deleteTables) {
      channel = channel.on('postgres_changes', { event: 'DELETE', schema: 'public', table }, scheduleRefresh);
    }
    channel.subscribe(status => {
      const isConnected = status === 'SUBSCRIBED';
      setConnected(isConnected);
      if (isConnected) scheduleRefresh();
    });

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') scheduleRefresh();
    };
    window.addEventListener('online', scheduleRefresh);
    window.addEventListener('focus', scheduleRefresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
      window.removeEventListener('online', scheduleRefresh);
      window.removeEventListener('focus', scheduleRefresh);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      void supabase.removeChannel(channel);
      setConnected(false);
    };
  }, [debounceMs, enabled, tableKey, deleteKey]); // Stable subscription definitions.

  return connected;
}
